/**
 * useIdentityBrief — derives the editorial copy block at the top of the
 * User Analytics page from the active identity-pillar state.
 *
 * Mirrors `usePostureBrief` exactly: takes a `tenantId` plus the metrics it
 * needs (score, deltas, topAction, criticalCount) and calls
 * `composeIdentityBrief`. The hook makes no API calls — it is a pure
 * memoized wrapper so the standfirst stays stable across renders for a given
 * (tenant, day, input shape).
 */

import { useMemo } from 'react'
import { useMsal } from '@azure/msal-react'
import {
  composeIdentityBrief,
  type IdentityBrief,
  type IdentityBriefInput,
  type Pillar,
} from '@/lib/identity-brief'

export interface UseIdentityBriefArgs {
  /**
   * Active tenant ID. Kept for call-site compatibility; the composer is
   * deterministic (same input, same brief) and no longer varies copy per tenant.
   */
  tenantId?: string
  /** MFA member adoption percent (0-100). Same number rendered on PillarIdentity. */
  score?: number | null
  /** 30-day score delta — drives the delta tag and the lede. */
  deltas?: { score?: number; days?: number }
  /** Highest-impact identity action — informs the headline and verdict. */
  topAction?: { pillar: Pillar; title: string; impact?: 'crit' | 'high' | 'med' }
  /** Members without MFA — drives the headline, lede, verdict and primary action. */
  criticalCount?: number
  /**
   * Enabled member Global Administrators without MFA (`UserInsights
   * .noMfaGlobalAdmins`, types/index.ts). Adds the lede's "and {g} are Global
   * admins" clause (blueprint §6.2 row 3).
   */
  noMfaGlobalAdmins?: number
  /** Where the primary button goes (MFA tab or users list); adds `primaryAction`. */
  primaryHref?: string
  /** The last User Analytics refresh: null when never, undefined while unknown (the provenance line). */
  assessedAt?: string | null
  /** No completed user scan (`UserInsights.hasData` false); not while loading. */
  noData?: boolean
  /** With `noData`, for a never-assessed tenant: the first-assessment inputs (role and schedule). */
  firstAssessment?: IdentityBriefInput['firstAssessment']
}

export function useIdentityBrief(args: UseIdentityBriefArgs = {}): IdentityBrief {
  const { accounts } = useMsal()
  const userEmail = accounts[0]?.username

  // Memoize on the value-shape rather than the object identity so the page
  // can rebuild `args` each render without retriggering composition.
  const deltaScore = args.deltas?.score
  const deltaDays = args.deltas?.days
  const topActionPillar = args.topAction?.pillar
  const topActionTitle = args.topAction?.title
  const topActionImpact = args.topAction?.impact
  const firstScanHref = args.firstAssessment?.scanHref
  const firstSettingsHref = args.firstAssessment?.settingsHref
  const firstSchedule = args.firstAssessment?.schedule
  const hasFirstAssessment = args.firstAssessment !== undefined

  return useMemo(
    () =>
      composeIdentityBrief({
        userEmail,
        tenantId: args.tenantId,
        score: args.score,
        deltas:
          deltaScore !== undefined || deltaDays !== undefined
            ? { score: deltaScore, days: deltaDays }
            : undefined,
        topAction:
          topActionPillar && topActionTitle
            ? { pillar: topActionPillar, title: topActionTitle, impact: topActionImpact }
            : undefined,
        criticalCount: args.criticalCount,
        noMfaGlobalAdmins: args.noMfaGlobalAdmins,
        primaryHref: args.primaryHref,
        assessedAt: args.assessedAt,
        noData: args.noData,
        firstAssessment: hasFirstAssessment
          ? { scanHref: firstScanHref, settingsHref: firstSettingsHref, schedule: firstSchedule }
          : undefined,
      }),
    [
      userEmail,
      args.tenantId,
      args.score,
      deltaScore,
      deltaDays,
      topActionPillar,
      topActionTitle,
      topActionImpact,
      args.criticalCount,
      args.noMfaGlobalAdmins,
      args.primaryHref,
      args.assessedAt,
      args.noData,
      hasFirstAssessment,
      firstScanHref,
      firstSettingsHref,
      firstSchedule,
    ],
  )
}
